import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import type { EventCategory } from '#~/analytics/constants/event-categories.js';
import type { EventName } from '#~/analytics/constants/event-names.js';
import type { EventProperties } from '#~/analytics/types/properties.js';
import { resolveLinkingSurface } from './linking-surface.js';

/** Default 60s from the device-telemetry contract. Product can move it; the event name stays. */
export const DEVICE_SHADOW_TIMEOUT_MS = 60_000;

type TrackFn = (name: EventName, category: EventCategory, properties: EventProperties) => void;

const timers = new Map<string, ReturnType<typeof setTimeout>>();

// One clock per Studio and agent: the same camera claimed into two Studios has
// two claims, and a shadow in one Studio says nothing about the other.
const timerKey = (agentId: string, studioId: string | undefined): string =>
  `${studioId ?? ''}:${agentId}`;

/**
 * Only an embedded camera grows a shadow device. A regular agent never emits
 * `device_shadowed`, so arming its timer reports a false timeout. Unknown is
 * not treated as embedded: the credential screen does not submit until the
 * agent record has loaded.
 */
export const shouldArmDeviceShadowTimeout = (embedded: boolean | undefined): boolean =>
  embedded === true;

/**
 * Arm claim-without-shadow.
 *
 * WHY: an embedded link that settles and then never emits `device_shadowed`
 * is a camera that did not appear. The clock starts when boot navigation
 * begins, after the registry poll, so that poll is not part of the 60s.
 * Broker disarms when the shadow emits in the same Studio. Re-arming the same
 * Studio and agent replaces that clock.
 *
 * Per rule: reference-rules-in-comments (ai.knowledge/global).
 */
export const armDeviceShadowTimeout = (input: {
  agentId: string;
  studioId?: string;
  track: TrackFn;
  timeoutMs?: number;
}): void => {
  disarmDeviceShadowTimeout(input.agentId, input.studioId);
  const key = timerKey(input.agentId, input.studioId);
  const timeoutMs = input.timeoutMs ?? DEVICE_SHADOW_TIMEOUT_MS;
  const handle = setTimeout(() => {
    timers.delete(key);
    input.track(EVENT_NAMES.DEVICE_SHADOW_TIMEOUT, EVENT_CATEGORIES.DEVICE, {
      agent_id: input.agentId,
      linking_surface: resolveLinkingSurface(),
      ...(input.studioId !== undefined && input.studioId !== ''
        ? { studio_id: input.studioId }
        : {})
    });
  }, timeoutMs);
  timers.set(key, handle);
};

/**
 * Stops only this Studio's clock for the agent. The Studio is required so a
 * caller cannot cancel every Studio's claim by leaving it out; an absent
 * Studio is its own key.
 */
export const disarmDeviceShadowTimeout = (agentId: string, studioId: string | undefined): void => {
  const key = timerKey(agentId, studioId);
  const handle = timers.get(key);
  if (handle === undefined) {
    return;
  }
  clearTimeout(handle);
  timers.delete(key);
};

/**
 * Whether a claim-without-shadow clock is still running for this agent. Lets
 * the e2e receipt read the production timer state right after a shadow instead
 * of sleeping out the window to prove a negative.
 */
export const isDeviceShadowTimeoutArmed = (
  agentId: string,
  studioId: string | undefined
): boolean => timers.has(timerKey(agentId, studioId));
